import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Minus, Plus, Search, ShoppingCart, Tag, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { useAuth } from "@/context/AuthContext";
import { request } from "@/lib/api";

const PAGE_SIZE = 25;
const CART_STORAGE_KEY = "ivory.labCart";

// The partner's file carries no categories, so these are search shortcuts
// rather than a taxonomy — one tap instead of typing the word. Chosen for what
// people actually order rather than what the list happens to open on.
const QUICK_SEARCHES = [
  "Thyroid",
  "Vitamin",
  "Diabetes",
  "Lipid",
  "Liver",
  "Kidney",
  "Haemogram",
  "Iron",
];

const SORTS = [
  { value: "price_low", label: "Price: low first" },
  { value: "name", label: "A–Z" },
  { value: "price_high", label: "Price: high first" },
  { value: "discount", label: "Biggest saving" },
];

function readStoredCart() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(CART_STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored.filter((id) => typeof id === "string") : [];
  } catch {
    // Private windows and cleared site data both land here. An empty cart is
    // the right answer; a crash is not.
    return [];
  }
}

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function LabTestsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState("price_low");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState({ tests: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cartIds, setCartIds] = useState(readStoredCart);
  const [cartDetail, setCartDetail] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const listTop = useRef(null);

  // Typing shouldn't fire a request per keystroke against a 1,400-row table.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartIds));
    } catch {
      // Storage being unavailable costs the cart between visits, nothing more.
    }
  }, [cartIds]);

  useEffect(() => {
    request("/lab-tests/categories", {}, token).then(setCategories).catch(() => setCategories([]));
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset), sort });
    if (debounced) params.set("search", debounced);
    if (category) params.set("category", category);
    request(`/lab-tests?${params}`, {}, token)
      .then((result) => !cancelled && setPage(result))
      .catch(() => !cancelled && setError("We couldn't load the test list. Please try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced, sort, category, offset, token]);

  const refreshCart = useCallback(() => {
    if (!cartIds.length) {
      setCartDetail(null);
      return;
    }
    // Priced by the server, always: the stored cart is only a list of ids and
    // may be weeks old.
    request("/lab-tests/cart/price", { method: "POST", body: JSON.stringify(cartIds) }, token)
      .then((result) => {
        setCartDetail(result);
        if (result.unavailable?.length) {
          setCartIds((current) => current.filter((id) => !result.unavailable.includes(id)));
        }
      })
      .catch(() => setCartDetail(null));
  }, [cartIds, token]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const inCart = useMemo(() => new Set(cartIds), [cartIds]);
  const toggle = (id) =>
    setCartIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const pageCount = Math.ceil((page.total || 0) / PAGE_SIZE) || 1;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function goToPage(next) {
    setOffset(Math.max(0, (next - 1) * PAGE_SIZE));
    listTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <section className="page-stack page-form" data-testid="lab-tests-page">
      <div className="page-heading">
        <p className="eyebrow">Discounted lab tests</p>
        <h1>Pathology at a members' price.</h1>
        <p>Collected from your home. Search the list, add what you need, and choose a time that suits you.</p>
      </div>

      {error && <p className="form-error" role="alert" data-testid="lab-tests-error">{error}</p>}

      <div className="lab-toolbar" ref={listTop}>
        <div className="lab-search">
          <Search size={17} />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by test name — e.g. thyroid, vitamin D"
            aria-label="Search tests"
            data-testid="lab-search-input"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} aria-label="Clear search" data-testid="lab-search-clear">
              <X size={15} />
            </button>
          )}
        </div>
        <div className="lab-filters">
          <select value={sort} onChange={(event) => { setSort(event.target.value); setOffset(0); }} aria-label="Sort tests" data-testid="lab-sort-select">
            {SORTS.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
          </select>
          {categories.length > 0 && (
            <select value={category} onChange={(event) => { setCategory(event.target.value); setOffset(0); }} aria-label="Filter by category" data-testid="lab-category-select">
              <option value="">All categories</option>
              {categories.map((item) => <option value={item} key={item}>{item}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="lab-quick-filters" data-testid="lab-quick-filters">
        {QUICK_SEARCHES.map((term) => (
          <button
            type="button"
            key={term}
            className={search.toLowerCase() === term.toLowerCase() ? "is-selected" : ""}
            onClick={() => setSearch(search.toLowerCase() === term.toLowerCase() ? "" : term)}
            data-testid={`lab-quick-${term.toLowerCase()}`}
          >
            {term}
          </button>
        ))}
      </div>

      <p className="lab-result-count" data-testid="lab-result-count">
        {loading ? "Searching…" : `${page.total.toLocaleString("en-IN")} test${page.total === 1 ? "" : "s"}${debounced ? ` matching “${debounced}”` : ""}`}
      </p>

      <ul className="lab-test-list" data-testid="lab-test-list">
        {page.tests.map((test) => (
          <li key={test.id} className={inCart.has(test.id) ? "is-in-cart" : ""} data-testid={`lab-test-${test.id}`}>
            <div className="lab-test-main">
              <strong>{test.name}</strong>
              <div className="lab-test-prices">
                <span className="lab-price">{money(test.price)}</span>
                <s className="lab-mrp">{money(test.mrp)}</s>
                <span className="lab-discount"><Tag size={12} />{test.discount_percent}% off</span>
              </div>
              {test.category && <small className="employee-muted">{test.category}</small>}
            </div>
            <Button
              type="button"
              variant={inCart.has(test.id) ? "secondary" : "outline"}
              size="sm"
              onClick={() => toggle(test.id)}
              data-testid={`lab-toggle-${test.id}`}
            >
              {inCart.has(test.id) ? <><Minus size={15} />Remove</> : <><Plus size={15} />Add</>}
            </Button>
          </li>
        ))}
        {!loading && !page.tests.length && (
          <li className="empty-state" data-testid="lab-no-results">
            Nothing matches “{debounced}”. Try a shorter word — searching “thyroid” finds more than “thyroid profile total”.
          </li>
        )}
      </ul>

      {pageCount > 1 && (
        <div className="lab-pagination" data-testid="lab-pagination">
          <Button type="button" variant="outline" size="sm" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)} data-testid="lab-prev-page">
            <ChevronLeft size={15} />Previous
          </Button>
          <span>Page {currentPage} of {pageCount.toLocaleString("en-IN")}</span>
          <Button type="button" variant="outline" size="sm" disabled={currentPage >= pageCount} onClick={() => goToPage(currentPage + 1)} data-testid="lab-next-page">
            Next
          </Button>
        </div>
      )}

      {cartIds.length > 0 && (
        <div className="lab-cart-bar" data-testid="lab-cart-bar">
          <div>
            <strong>{cartIds.length} test{cartIds.length === 1 ? "" : "s"}</strong>
            {cartDetail && <span>{money(cartDetail.amount)} · saving {money(cartDetail.mrp_total - cartDetail.tests_total)}</span>}
          </div>
          <Button type="button" onClick={() => setCartOpen(true)} data-testid="lab-view-cart-button">
            <ShoppingCart size={16} />Review
          </Button>
        </div>
      )}

      {cartOpen && (
        <LabCart
          detail={cartDetail}
          onClose={() => setCartOpen(false)}
          onRemove={toggle}
          onCheckout={() => navigate("/lab-tests/checkout")}
        />
      )}
    </section>
  );
}

function LabCart({ detail, onClose, onRemove, onCheckout }) {
  return (
    <div className="lab-cart-sheet" role="dialog" aria-label="Your cart" data-testid="lab-cart-sheet">
      <div className="lab-cart-sheet__inner">
        <div className="upload-summary">
          <strong>Your tests</strong>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="lab-cart-close"><X size={15} />Close</Button>
        </div>
        <ul className="lab-cart-items">
          {(detail?.items || []).map((item) => (
            <li key={item.id} data-testid={`lab-cart-item-${item.id}`}>
              <div>
                <strong>{item.name}</strong>
                <small><s>{money(item.mrp)}</s> {money(item.price)}</small>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(item.id)} aria-label={`Remove ${item.name}`} data-testid={`lab-cart-remove-${item.id}`}>
                <X size={14} />
              </Button>
            </li>
          ))}
        </ul>
        {detail && (
          <div className="lab-cart-total">
            <div><span>List price</span><s>{money(detail.mrp_total)}</s></div>
            <div><span>You save</span><strong className="lab-saving">{money(detail.mrp_total - detail.tests_total)}</strong></div>
            {/* Charged once per order however many tests are in the cart, so
                it belongs in the cart total rather than on any test. */}
            <div data-testid="lab-cart-collection-fee"><span>Home visit charge</span><strong>{money(detail.collection_fee)}</strong></div>
            <div className="lab-cart-total__due"><span>To pay</span><strong>{money(detail.amount)}</strong></div>
          </div>
        )}
        <Button type="button" disabled={!detail?.items?.length} onClick={onCheckout} data-testid="lab-checkout-button">
          Continue to collection details
        </Button>
      </div>
    </div>
  );
}

export { CART_STORAGE_KEY, money, readStoredCart };
